// `extended-title-class-ux-recommendations` + `category-tip`, from BOTH builders.
//
// Upstream carries this markup twice — `search.page.tpl.html:184-215` and
// `client-contact-search.page.tpl.html:112-150` — identical but for the feature-flag guard
// the contact form does not have. That duplication is the thing this repo exists to remove,
// so it is one component here and the guard is the caller's.
//
// ⚠️ THE BLOCK HAS AN EMPTY STATE AND IT IS NOT OPTIONAL. Once a row has a title the block
// renders whatever the answer is: "Getting recommended titles...", "No recommended titles",
// or the two sections. Rendering it only when there are recommendations — which is the
// obvious reading — leaves the contact form silently missing a section production shows.
import type { ReactElement } from 'react'
import type { TitleRecommendation } from '@woo-ext/api'
import type { Tag } from '../common/TagsInput.tsx'

/** The two sections, in template order. `titleClass` is what splits them. */
const TITLE_RECOMMENDATION_SECTIONS = [
  { key: 'categories', className: 'categories', title: 'Extended Categories', isCategory: true },
  { key: 'titles', className: 'titles', title: 'Specific Titles', isCategory: false },
] as const

export interface TitleRecommendationsProps {
  rows: readonly TitleRecommendation[]
  loading?: boolean
  onSelect?: (title: string) => void
}

export function TitleRecommendations({ rows, loading = false, onSelect }: TitleRecommendationsProps): ReactElement {
  return (
    <div className="extended-title-class-ux-recommendations">
      <label className="extended-title-class-ux-recommendations-title">
        <span>Suggestions</span>
        <i className="cursor-pointer info-btn suggestions-tooltip-info-btn fa fa-info-circle" />
      </label>
      <div className="extended-title-class-ux-recommendations-content">
        {loading ? <div className="loading">Getting recommended titles...</div> : null}
        {!loading && rows.length === 0 ? <div className="no-suggestions">No recommended titles</div> : null}
        {TITLE_RECOMMENDATION_SECTIONS.map(({ key, className, title, isCategory }) => {
          const section = rows.filter((entry) => entry.titleClass === isCategory)
          if (loading || section.length === 0) return null
          return (
            <div className={`${className} recommendation-section`} key={key}>
              <label className="recommendation-section-title">
                <span>{title}</span>
                <i className="cursor-pointer info-btn fa fa-info-circle" />
              </label>
              <div className="recommendation-section-recommendations">
                {section.map((entry) => (
                  <span
                    className="title-suggestion"
                    key={entry.title}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect?.(entry.title)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      onSelect?.(entry.title)
                    }}
                  >
                    {entry.title}
                  </span>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * `category-tip` — the line above the Suggestions block, in both builders and ported to
 * neither.
 *
 * ⚠️ IT DISAPPEARS THE MOMENT A CATEGORY IS CHOSEN. `!foe.selectedTitleCategoryRecommendation`
 * is `titles.some(t => t.titleClass)` upstream (`client-contact-search.page.component.ng1.ts:976`),
 * so the tip is advice while it is still worth giving and gone once it is taken — a tip that
 * stays on screen after the user has done what it asks reads as a warning that will not clear.
 *
 * The one part of upstream's condition that has no equivalent here is
 * `ctrl.form.titleCriteria['titles' + $index].$valid`, which is AngularJS's leftover-text
 * validator: this port's `TagsInput` cannot hold leftover text on an
 * add-from-autocomplete-only field, so the clause is satisfied by construction.
 */
export function showCategoryTip(
  titles: readonly Tag[] | undefined,
  recommendations: readonly TitleRecommendation[],
): boolean {
  if ((titles?.length ?? 0) === 0) return false
  if (!recommendations.some((entry) => entry.titleClass)) return false
  return !(titles ?? []).some((tag) => tag.source?.['titleClass'] === true)
}

export function CategoryTip(): ReactElement {
  return (
    <div className="category-tip">
      {/* Upstream's `<i class="info-btn fas fa-lightbulb-exclamation">` is commented out in
          the template; the span is the whole of it. */}
      <span>Tip: For a more comprehensive search, select at least one category</span>
    </div>
  )
}
